/**
 * CallClientModal - Modal for selecting grace minutes when calling a client
 */

import { useState } from 'react';
import { X, Clock, Scissors, Phone, Baby, PersonStanding } from 'lucide-react';

interface CallClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (graceMinutes: 5 | 10 | 15 | 20) => void;
  onStartServiceDirectly?: () => void;
  ticketNumber?: string;
  clientName?: string;
  clientPhone?: string | null;
  // Companion fields
  isCompanion?: boolean;
  companionName?: string;
  companionType?: 'child' | 'elder' | 'other';
}

const TIME_OPTIONS: Array<{value: 5 | 10 | 15 | 20; label: string}> = [
  { value: 5, label: '5 minutos' },
  { value: 10, label: '10 minutos' },
  { value: 15, label: '15 minutos' },
  { value: 20, label: '20 minutos' },
];

export function CallClientModal({
  isOpen,
  onClose,
  onConfirm,
  onStartServiceDirectly,
  ticketNumber,
  clientName,
  clientPhone,
  isCompanion,
  companionName,
  companionType
}: CallClientModalProps) {
  const [selectedTime, setSelectedTime] = useState<5 | 10 | 15 | 20>(20);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(selectedTime);
    onClose();
  };

  const handleStartDirectly = () => {
    if (onStartServiceDirectly) {
      onStartServiceDirectly();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-client-modal-title"
        className="bg-surface border border-hairline rounded-2xl shadow-xl w-full max-w-md mx-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-hairline">
          <h2 id="call-client-modal-title" className="font-sans text-lg font-semibold text-ink">
            Llamar Cliente
          </h2>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            className="text-ink-subtle hover:text-ink transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Client Info */}
          {(ticketNumber || clientName || companionName) && (
            <div className="mb-6 p-3 rounded-lg bg-surface-2 border border-hairline">
              {ticketNumber && (
                <p className="text-sm font-medium text-ink">
                  Turno: {ticketNumber}
                </p>
              )}
              {isCompanion && companionName ? (
                <>
                  <p className="text-sm text-ink-muted flex items-center gap-1.5">
                    {companionType === 'child'
                      ? <Baby className="w-3.5 h-3.5 text-ink-subtle" aria-hidden="true" />
                      : <PersonStanding className="w-3.5 h-3.5 text-ink-subtle" aria-hidden="true" />}
                    {companionName}
                  </p>
                  {clientName && (
                    <p className="text-xs text-ink-subtle mt-1">
                      Acompañante de {clientName}
                    </p>
                  )}
                </>
              ) : clientName && (
                <p className="text-sm text-ink-muted">
                  Cliente: {clientName}
                </p>
              )}
              {clientPhone && (
                <a
                  href={`tel:${clientPhone}`}
                  className="mt-1 text-sm flex items-center gap-1 hover:underline text-accent"
                >
                  <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                  {clientPhone}
                </a>
              )}
            </div>
          )}

          {/* Already Here - Start Service Directly */}
          {onStartServiceDirectly && (
            <div className="mb-4">
              <button
                onClick={handleStartDirectly}
                className="w-full p-4 rounded-lg border border-accent/50 bg-accent/5 text-ink hover:border-accent transition-colors flex items-center justify-center gap-3"
              >
                <Scissors className="w-6 h-6 text-accent" aria-hidden="true" />
                <div className="text-left">
                  <div className="font-bold text-base text-ink">Ya está en la pelu</div>
                  <div className="text-xs text-ink-muted">Comenzar corte directamente</div>
                </div>
              </button>
            </div>
          )}

          {/* Divider */}
          {onStartServiceDirectly && (
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-hairline"></div>
              <span className="text-xs text-ink-subtle">o llamar y esperar</span>
              <div className="flex-1 h-px bg-hairline"></div>
            </div>
          )}

          {/* Time Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-ink-muted">
              <Clock className="inline-block w-4 h-4 mr-2 text-ink-subtle" aria-hidden="true" />
              ¿Cuánto tiempo tiene para llegar?
            </label>

            <div className="grid grid-cols-2 gap-3">
              {TIME_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setSelectedTime(option.value)}
                  className={`
                    p-4 rounded-lg border transition-colors
                    ${selectedTime === option.value
                      ? 'border-accent bg-accent/10 text-accent'
                      : 'border-hairline bg-surface-2 text-ink-muted hover:border-hairline-strong hover:text-ink'
                    }
                  `}
                >
                  <div className="text-2xl font-semibold tabular-nums">{option.value}</div>
                  <div className="text-xs mt-1">minutos</div>
                </button>
              ))}
            </div>
          </div>

          {/* Info Text */}
          <p className="mt-4 text-xs text-ink-subtle">
            El cliente recibirá una notificación y tendrá {selectedTime} minutos para presentarse.
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-hairline">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-ink bg-surface-2 border border-hairline rounded-lg hover:bg-surface-3 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 px-4 py-2 text-sm font-medium text-on-accent bg-accent rounded-lg hover:bg-accent-hover transition-colors tabular-nums"
          >
            Llamar Cliente ({selectedTime} min)
          </button>
        </div>
      </div>
    </div>
  );
}
